import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';

/*
 * ---------------------------------------------------------------------------
 * SESTO. Patch 21.09.2026 — Mobile Fullscreen Zoom
 *
 * Diese Datei ist eine Horizon-Kerndatei. Bei einem Theme-Update wird sie
 * ueberschrieben. Dann die drei markierten Stellen (FIX 1, FIX 2, FIX 3)
 * erneut einsetzen.
 *
 * FIX 1 — MINIMUM-ZOOM WAR ALS "COVER" GERECHNET
 * #getMinZoom() gab bisher Math.max(containerW/imageW, containerH/imageH)
 * zurueck. Das ist die Formel fuer object-fit: cover. Das Bild wird hier aber
 * mit object-fit: contain dargestellt, und #getImageDimensions() liefert
 * bereits den fertigen contain-Kasten. Auf einem Portrait-Handy ist der
 * Dialog 375x812 (Seitenverhaeltnis 0.46), SESTOs Produktbilder sind 2048x2048
 * (1.0). Der contain-Kasten ist damit 375x375 und die Cover-Formel ergab
 * 812/375 + 0.001 = 2.1663 als KLEINSTEN erlaubten Zoom.
 *
 * Folge: Der Viewer oeffnete bei 2.17x, die Brille war links und rechts
 * abgeschnitten, und Herauszoomen auf das ganze Produkt war rechnerisch
 * unmoeglich. DEFAULT_ZOOM (1.5) kam nie zum Tragen, weil
 * #constrainTranslation() sofort nach oben klemmte.
 *
 * Jetzt: Der Boden ist der Massstab, bei dem der contain-Kasten vollstaendig
 * in den Container passt. Weil #getImageDimensions() den contain-Kasten selbst
 * liefert, ist dieser Massstab per Definition der unskalierte Zustand
 * (MIN_ZOOM). Die Rechnung bleibt trotzdem aus den echten Render-Dimensionen
 * abgeleitet und funktioniert fuer quadratische, hochformatige und
 * querformatige Bilder in jedem Viewport.
 *
 * FIX 2 — LIFECYCLE: LISTENER KAMEN NACH EINEM RECONNECT NICHT ZURUECK
 * disconnectedCallback() rief #controller.abort() und meldete damit alle
 * Gesten-Listener ab, liess #initialized aber auf true stehen. Beim
 * Wiedereinhaengen stieg #initEventListeners() an "if (this.#initialized)
 * return;" sofort aus — der AbortController war tot, ein neuer wurde nie
 * erzeugt. Ergebnis: Sobald irgendetwas die Galerie einmal aus dem DOM nimmt
 * und wieder einsetzt (Variant-Update ersetzt die <media-gallery>, ein
 * Section-Rerender, App-Skripte), war Pinch und Pan dauerhaft tot, ohne dass
 * man es der Komponente ansah: die CSS-Variablen standen vom ersten Connect
 * noch am Element.
 *
 * Jetzt raeumt #teardown() vollstaendig auf und stellt den Ausgangszustand
 * wieder her: abort, FRISCHER AbortController, #initialized zurueck auf false,
 * ResizeObserver getrennt, offener AnimationFrame abgebrochen. Der naechste
 * connectedCallback() registriert sauber neu. Doppelte Listener sind
 * ausgeschlossen, weil #initEventListeners() weiterhin ueber #initialized
 * wacht und ein Reconnect ohne vorheriges Disconnect nicht vorkommt.
 *
 * FIX 3 — TAP ZUM SCHLIESSEN DARF NICHT STERBEN
 * #handleTouchStart() rief preventDefault() bedingungslos fuer JEDE
 * Beruehrung. Solange die Listener (durch FIX 2) ohnehin tot waren, fiel das
 * nicht auf. Mit reparierten Listenern haette es den Klick gekillt, den
 * Horizon am <li on:click="/close"> haengen hat: ein abgebrochenes touchstart
 * unterdrueckt das nachfolgende click-Event.
 *
 * Jetzt wird nur noch abgebrochen, wenn wirklich eine Geste laeuft: zwei
 * Finger (Pinch) immer, ein Finger nur, wenn ueberhaupt etwas zu verschieben
 * ist — also wenn weiter als bis zum contain-Minimum hineingezoomt wurde. Am
 * Minimum bleibt ein Tap ein Tap und schliesst den Viewer wie bisher.
 *
 * NICHT ENTFERNT: touch-action: none in blocks/_product-media-gallery.liquid.
 * Der dortige Kommentar vom 06.09.2026 nennt eine falsche Ursache (Compositor),
 * die Deklaration selbst ist aber die korrekte Art, eine eigene Pinch-/Pan-
 * Flaeche gegen die native Browser-Geste abzugrenzen — ohne sie zoomt iOS
 * Safari bei zwei Fingern die Seite statt das Bild. Der Kommentar wurde dort
 * richtiggestellt, die Regel bleibt.
 * ---------------------------------------------------------------------------
 */

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

/**
 * Toleranz beim Vergleich mit dem Minimum-Zoom. Gleitkommarechnung aus
 * clientWidth/clientHeight trifft die 1 nicht immer exakt.
 */
const ZOOM_EPSILON = 0.01;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();
    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    this.#image?.addEventListener('load', this.#updateTransform, { signal });
  }

  disconnectedCallback() {
    this.#teardown();
  }

  /*
   * FIX 2 — vollstaendiges Aufraeumen. Wichtig ist der FRISCHE AbortController:
   * ein abgebrochener Controller kann nie wieder Listener tragen, und ohne
   * Ruecksetzen von #initialized wuerde #initEventListeners() beim naechsten
   * connectedCallback() kommentarlos aussteigen.
   */
  #teardown() {
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#initialized = false;
    this.#isDragging = false;
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * Gibt es ueberhaupt etwas zu verschieben? Am contain-Minimum liegt das
   * ganze Bild im Container — dann ist ein Einfinger-Zug kein Pan, sondern ein
   * Tap, der den Viewer schliessen soll.
   * @returns {boolean}
   */
  #canPan() {
    const minZoom = this.#getMinZoom();
    if (minZoom == null) return false;
    return this.#scale > minZoom + ZOOM_EPSILON;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      // FIX 3: Pinch gehoert immer uns.
      preventDefault(event);

      const [point1, point2] = Array.from(event.touches).map(touchToPoint);
      if (!point1 || !point2) return;
      this.#startZoomGesture(point1, point2);
    } else if (event.touches.length === 1) {
      // FIX 3: Einfinger nur abfangen, wenn gepannt werden kann. Sonst bleibt
      // das nachfolgende click-Event erhalten (Horizon: on:click="/close").
      if (!this.#canPan()) {
        this.#isDragging = false;
        return;
      }
      preventDefault(event);

      const point = touchToPoint(event.touches[0]);
      if (!point) return;

      this.#startDragGesture(point);
    }
  };

  /**
   * Start a zoom gesture with two points
   * @param {Point} point1
   * @param {Point} point2
   */
  #startZoomGesture(point1, point2) {
    this.#initialDistance = getDistance(point1, point2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single point
   * @param {Point} point
   */
  #startDragGesture(point) {
    this.#startPosition = { x: point.x, y: point.y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const isZooming = event.touches.length === 2;
    const isDragging = event.touches.length === 1 && this.#isDragging;

    // FIX 3: nur abbrechen, wenn tatsaechlich eine Geste laeuft.
    if (!isZooming && !isDragging) return;
    preventDefault(event);

    if (isZooming) {
      this.#processZoomGesture(event);
    } else {
      this.#processDragGesture(event);
    }
  };

  /**
   * Process zoom gesture from touch event
   * @param {TouchEvent} event
   */
  #processZoomGesture(event) {
    const [point1, point2] = Array.from(event.touches).map(touchToPoint);
    if (!point1 || !point2) return;
    if (!this.#initialDistance) return;

    const currentMidpoint = getMidpoint(point1, point2);
    const currentDistance = getDistance(point1, point2);
    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    const distanceFromCenter = {
      x: currentMidpoint.x - containerCenter.x,
      y: currentMidpoint.y - containerCenter.y,
    };

    // Calculate how the image position needs to change to keep the midpoint stationary
    // The correction factor accounts for the change in scale
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch event
   * @param {TouchEvent} event
   */
  #processDragGesture(event) {
    const point = touchToPoint(event.touches[0]);
    if (!point) return;

    // Calculate new translation
    this.#translate = {
      x: this.#startTranslate.x + (point.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (point.y - this.#startPosition.y) / this.#scale,
    };

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
    }
  };

  /**
   * FIX 1 — kleinster erlaubter Massstab.
   *
   * #getImageDimensions() liefert bereits den contain-Kasten, also die Groesse,
   * in der das Bild unskaliert dargestellt wird. Der kleinste sinnvolle
   * Massstab ist damit genau der, bei dem dieser Kasten vollstaendig in den
   * Container passt — aus den echten Render-Dimensionen gerechnet und nie
   * groesser als der unskalierte Zustand.
   *
   * Vorher: Math.max(...) + 0.001 (Cover-Logik, auf Portrait-Handy 2.1663).
   * @returns {number | null}
   */
  #getMinZoom = () => {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions || !containerWidth || !containerHeight) return null;
    const { width: imageWidth, height: imageHeight } = imageDimensions;
    if (!imageWidth || !imageHeight) return null;

    const fitScale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);

    return Math.min(MIN_ZOOM, fitScale);
  };

  /**
   * Get the dimensions of the image
   * @returns {{ width: number, height: number } | null}
   */
  #getImageDimensions = () => {
    const containerRect = this.getBoundingClientRect();
    if (!this.#image) return null;
    const { naturalWidth, naturalHeight } = this.#image;
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) return null;

    const containerAspect = containerRect.width / containerRect.height;
    const naturalAspect = naturalWidth / naturalHeight;

    let imageWidth, imageHeight;
    if (naturalAspect > containerAspect) {
      // Image is wider than container (relative to height)
      imageWidth = containerRect.width;
      imageHeight = imageWidth / naturalAspect;
    } else {
      // Image is taller than container (relative to width)
      imageHeight = containerRect.height;
      imageWidth = imageHeight * naturalAspect;
    }

    if (imageWidth === 0 || imageHeight === 0) return null;

    return { width: imageWidth, height: imageHeight };
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight) return;

    const minZoom = this.#getMinZoom();
    if (minZoom == null) return;

    this.#scale = clamp(this.#scale, minZoom, MAX_ZOOM);

    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions) return;

    const { width: imageWidth, height: imageHeight } = imageDimensions;

    const scaledWidth = imageWidth * this.#scale;
    const scaledHeight = imageHeight * this.#scale;

    // Calculate how much the image extends beyond container
    const excessWidth = Math.max(0, scaledWidth - containerWidth);
    const excessHeight = Math.max(0, scaledHeight - containerHeight);

    const maxTranslateX = excessWidth / (2 * this.#scale);
    const maxTranslateY = excessHeight / (2 * this.#scale);

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  destroy() {
    this.#teardown();
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * Convert a Touch object to a Point object
 * @param {Touch | undefined} touch
 * @returns {Point | undefined}
 */
function touchToPoint(touch) {
  if (!touch) return undefined;
  return { x: touch.clientX, y: touch.clientY };
}

/**
 * Calculate the distance between two points
 * @param {Point} point1 - First point
 * @param {Point} point2 - Second point
 * @returns {number} The distance between the points
 */
function getDistance(point1, point2) {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate the midpoint between two points
 * @param {Point} point1 - First point
 * @param {Point} point2 - Second point
 * @returns {Point} The midpoint
 */
function getMidpoint(point1, point2) {
  return {
    x: (point1.x + point2.x) / 2,
    y: (point1.y + point2.y) / 2,
  };
}
